// src/utils/co2Calculator.js

/**
 * CO2 impact estimation utilities for FarmMark.
 *
 * Central module for all CO2-related calculations.
 * Adjusting the constants here automatically updates all UI surfaces
 * (CO2 tab, My Impact, export, etc.).
 *
 * Methodology (conservative):
 *   EPA + WRAP food waste lifecycle analysis estimates ~2.5 kg CO2e
   *   per kg of food sold (accounts for production, transport,
   *   and avoided methane from decomposition).
 */

import {
  CO2_KG_PER_KG_FOOD_SOLD,
  CO2_KG_PER_TREE_PER_YEAR,
  CO2_KG_PER_CAR_KM,
  AVG_MEAL_WEIGHT_KG,
} from "../constants/farmMarkConstants";

/**
 * Estimate CO2 saved (kg) for a given amount of food claimed.
 * @param {number} foodKg - weight of food claimed in kg
 * @returns {number} estimated CO2 saved in kg
 */
export function estimateCO2Saved(foodKg) {
  const kg = Number(foodKg);
  if (!Number.isFinite(kg) || kg <= 0) return 0;
  return Math.round(kg * CO2_KG_PER_KG_FOOD_SOLD * 100) / 100;
}

/**
 * Build human-readable equivalence metrics from a total CO2 saved figure.
 * @param {number} co2Kg - total CO2 saved in kg
 * @returns {{ treesPlanted: number, carKmAvoided: number, mealsRescued: number }}
 */
export function co2Equivalents(co2Kg) {
  const n = Number(co2Kg);
  if (!Number.isFinite(n) || n <= 0) {
    return { treesPlanted: 0, carKmAvoided: 0, mealsRescued: 0 };
  }
  return {
    treesPlanted: Math.round((n / CO2_KG_PER_TREE_PER_YEAR) * 10) / 10,
    carKmAvoided: Math.round(n / CO2_KG_PER_CAR_KM),
    mealsRescued: Math.round(n / (CO2_KG_PER_KG_FOOD_SOLD * AVG_MEAL_WEIGHT_KG)),
  };
}
